import { pick } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { AnalyticsFilter } from '../Analytics/AnalyticsFilter';
import { AnalyticsDateFilter } from '../Analytics/AnalyticsDateFilter';
import { AnalyticsCurrencyFilter } from '../Analytics/AnalyticsCurrencyFilter';
import { AnalyticsToggler } from '../Analytics/AnalyticsToggler';
import { FilterSeparator } from '../../filters/FilterSeparator';
import { getCountryName } from '../../../utils/textHelper';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import {
  getRelevantRetailersForOrderTypeFilter,
  getSalesChannelsOptions,
  doesSupplierSupportMultipleCurrencies,
} from '../Analytics/utils';
import { useShouldDisplayOrderTypeFilter } from '../Analytics/hooks/useShouldDisplayOrderTypeFilter';
import { ANALYTICS_SET_CURRENCY, ANALYTICS_SET_FILTER, ANALYTICS_SET_REPORTS } from '../../../store/reducers/analytics';
import { CountryIcon, ReportsWarehouseIcon, SalesChannelIcon } from '../../icons/analytics';
import { REPORT_FILTERS, dateTypes, biEventNameMapping } from '../../tables/reports/consts';
import { FunnelIcon } from '../../icons/analytics/FunnelIcon';
import { orderTypeOptions } from '../../../constants/analytics';

import styles from './ReportsPage.module.scss';

export const availableReportFilters = {
  dateType: 'Date Type',
  salesChannels: 'Sales Channels',
  dateRange: 'Date Range',
  currencies: 'Currency',
  byDateType: 'By date type',
};

export function ReportsFilters({ shownFilters = Object.keys(availableReportFilters), reportType, byDateTypes }) {
  const dispatch = useDispatch();
  const { analytics, warehouses } = useSelector((state) => pick(state, ['analytics', 'warehouses']));
  const { data: warehousesData } = warehouses;
  const mixpanel = useMixpanel();
  let {
    countries,
    currentCurrency,
    retailerNamesByIds,
    filters: {
      retailerIds,
      byDateType,
      countries: selectedCountries,
      orderTypes: selectedOrderTypes,
      warehouseIds: selectedWarehouses,
    },
    retailers,
  } = analytics;
  const dateType = useSelector((state) => state.analytics.reports[reportType]?.dateType);
  const { settings: supplierSettings, supplier_retailers: supplierRetailers } = useSelector(
    (state) => state.role.supplier,
  );
  const [shouldDisableOrderTypeFilter, setShouldDisableOrderTypeFilter] = useState(false);
  const showCurrenciesToggler = doesSupplierSupportMultipleCurrencies(supplierSettings);
  const { shouldDisplayOrderTypeFilter, retailerIdByOrderType } = useShouldDisplayOrderTypeFilter();
  useEffect(() => {
    if (!showCurrenciesToggler && supplierSettings?.analytics?.supported_currencies?.length) {
      dispatch({
        type: ANALYTICS_SET_CURRENCY,
        payload: { currency: supplierSettings.analytics.supported_currencies[0] },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSettings]);

  if (retailerIds.length === 0) {
    retailerIds = Object.keys(retailerNamesByIds).map(Number);
  }

  if (selectedWarehouses.length === 0) {
    selectedWarehouses = warehousesData.map((warehouse) => warehouse.id);
  }

  const salesChannelsOptions = useMemo(() => {
    const relevantRetailers = getRelevantRetailersForOrderTypeFilter(
      retailers,
      selectedOrderTypes,
      retailerIdByOrderType,
    );
    return getSalesChannelsOptions(supplierRetailers, retailers, relevantRetailers);
  }, [selectedOrderTypes, retailers, supplierRetailers, retailerIdByOrderType]);

  const countriesOptions = useMemo(() => {
    return Object.entries(countries)
      .map(([countryCode, name]) => ({
        value: countryCode,
        label: getCountryName(name, countryCode),
      }))
      .sort((countryA, countryB) => countryA.label.localeCompare(countryB.label));
  }, [countries]);

  const warehousesOptions = useMemo(() => {
    return (warehousesData || [])
      .map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }))
      .sort((warehouseA, warehouseB) => warehouseA.label.localeCompare(warehouseB.label));
  }, [warehousesData]);

  function reportBi(dateType) {
    reportBusinessActionToMixpanel(mixpanel, `${biEventNameMapping[reportType]}-report-toggle`, {
      chosenType: dateType,
    });
  }
  const areAllRetailerOptionsWithSingleOrderType = useCallback(
    (retailerIdsWithOrderType) => {
      return (
        retailerIds.every((retailerId) => retailerIdsWithOrderType.includes(retailerId)) &&
        retailerIds.length < retailerIdsWithOrderType.length
      );
    },
    [retailerIds],
  );

  const updateDisablingOrderTypeFilter = useCallback(() => {
    if (!retailerIdByOrderType) {
      setShouldDisableOrderTypeFilter(false);
      return;
    }
    if (Object.values(retailerIdByOrderType).find((ids) => areAllRetailerOptionsWithSingleOrderType(ids))) {
      setShouldDisableOrderTypeFilter(true);
      return;
    }
    setShouldDisableOrderTypeFilter(false);
  }, [areAllRetailerOptionsWithSingleOrderType, retailerIdByOrderType]);

  useEffect(() => {
    updateDisablingOrderTypeFilter();
  }, [updateDisablingOrderTypeFilter]);

  const onChangeOrderType = (orderTypes) => {
    let retailerIdsToUpdate = retailers.map(({ id }) => id);
    if (orderTypes.length === 1) {
      retailerIdsToUpdate = retailerIdsToUpdate.filter((retailerId) =>
        retailerIdByOrderType[orderTypes].includes(retailerId),
      );
    }
    const payload = {
      orderTypes,
      retailerIds: retailerIdsToUpdate,
    };
    dispatch({ type: ANALYTICS_SET_FILTER, payload });
  };

  return (
    <div className={styles.filters}>
      {shownFilters.includes(REPORT_FILTERS.DATE_TYPE) && (
        <AnalyticsToggler
          selected={dateType}
          onChange={(dateType) => {
            reportBi(dateType);
            dispatch({ type: ANALYTICS_SET_REPORTS, payload: { reportType, data: { dateType } } });
          }}
          options={Object.entries(dateTypes).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
        />
      )}

      {shownFilters.includes(REPORT_FILTERS.BY_DATE_TYPE) && byDateTypes && (
        <AnalyticsToggler
          options={byDateTypes}
          selected={byDateType}
          onChange={(byDateType) => {
            reportBi(byDateType);
            dispatch({
              type: ANALYTICS_SET_FILTER,
              payload: { byDateType },
            });
          }}
        />
      )}

      {shownFilters.includes(REPORT_FILTERS.DATE_RANGE) && (
        <>
          <AnalyticsDateFilter
            showToday={false}
            onChange={(startDate, endDate) =>
              dispatch({
                type: ANALYTICS_SET_FILTER,
                payload: { dates: { start: new Date(startDate), end: new Date(endDate) } },
              })
            }
          />
          <FilterSeparator />
        </>
      )}

      {shownFilters.includes(REPORT_FILTERS.COUNTRIES) && (
        <AnalyticsFilter
          title="Country"
          filterKey="countries"
          icon={<CountryIcon />}
          options={countriesOptions}
          onChange={(value) => {
            dispatch({ type: ANALYTICS_SET_FILTER, payload: { countries: value } });
          }}
          selected={selectedCountries}
          withSearch={countriesOptions.length > 2}
        />
      )}

      {shownFilters.includes(REPORT_FILTERS.WAREHOUSES) && (
        <AnalyticsFilter
          title="Warehouses"
          filterKey="warehouses"
          icon={<ReportsWarehouseIcon />}
          options={warehousesOptions}
          onChange={(value) => {
            dispatch({ type: ANALYTICS_SET_FILTER, payload: { warehouseIds: value } });
          }}
          selected={selectedWarehouses}
          withSearch={warehousesOptions.length > 2}
        />
      )}

      {shouldDisplayOrderTypeFilter && (
        <AnalyticsFilter
          title="Type"
          icon={<FunnelIcon />}
          filterKey="orderTypes"
          options={orderTypeOptions}
          onChange={onChangeOrderType}
          selected={selectedOrderTypes}
          withSearch={false}
          disabled={shouldDisableOrderTypeFilter}
        />
      )}

      {shownFilters.includes(REPORT_FILTERS.SALES_CHANNELS) && (
        <AnalyticsFilter
          title={availableReportFilters.salesChannels}
          icon={<SalesChannelIcon />}
          onChange={(values) => dispatch({ type: ANALYTICS_SET_FILTER, payload: { retailerIds: values } })}
          options={salesChannelsOptions}
          selected={retailerIds}
          withSearch={salesChannelsOptions.length > 2}
        />
      )}

      {shownFilters.includes(REPORT_FILTERS.CURRENCIES) && showCurrenciesToggler && (
        <>
          <FilterSeparator />
          <AnalyticsCurrencyFilter
            selected={currentCurrency}
            onChange={(value) => dispatch({ type: ANALYTICS_SET_CURRENCY, payload: { currency: value } })}
          />
        </>
      )}
    </div>
  );
}

ReportsFilters.propTypes = {
  shownFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  reportType: PropTypes.string.isRequired,
  byDateTypes: PropTypes.arrayOf(PropTypes.object),
};
